import React, { useState } from 'react';
import { Sparkles, TrendingUp, HelpCircle, Layers, Award, AlertCircle, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { useMenuEngineering } from '../../hooks/useReports';
import { MenuEngineeringItem } from '../../services/ReportsService';

interface Props {
  from: string;
  to: string;
}

function formatCOP(val: number) {
  return '$ ' + Math.round(val).toLocaleString('es-CO');
}

export function MenuEngineeringTab({ from, to }: Props) {
  const { data, isLoading } = useMenuEngineering(from, to);
  const [selectedQuadrant, setSelectedQuadrant] = useState<string>('ALL');

  if (isLoading) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-sm font-medium text-text-muted">Analizando rentabilidad y popularidad de recetas...</p>
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="rounded-3xl border border-border bg-surface p-12 text-center shadow-sm">
        <Sparkles className="mx-auto mb-3 h-10 w-10 text-text-muted" />
        <p className="text-base font-semibold text-text">Sin ventas registradas en este período</p>
        <p className="text-xs text-text-muted mt-1">
          No hay órdenes pagadas para calcular la matriz de ingeniería de menú entre {from} y {to}.
        </p>
      </div>
    );
  }

  const filteredItems = selectedQuadrant === 'ALL'
    ? data.items
    : data.items.filter((it) => it.quadrant === selectedQuadrant);

  return (
    <div className="space-y-6">
      {/* ── Banner Explicativo de la Matriz BCG ── */}
      <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-primary/20 p-1.5 text-primary">
                <Sparkles className="h-4 w-4" />
              </span>
              <h2 className="text-base font-bold text-text">Ingeniería de Menú (Matriz BCG Kasavana & Smith)</h2>
              <span className="rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                Rentabilidad vs Popularidad
              </span>
            </div>
            <p className="text-xs text-text-muted max-w-2xl">
              Cruza el volumen de unidades vendidas con el margen de ganancia real calculado a partir del escandallo de tus recetas para indicarte qué platos cuidar, cuáles subir de precio y cuáles reemplazar.
            </p>
          </div>

          <div className="flex items-center gap-3 bg-surface/80 rounded-2xl border border-border px-3 py-2 text-xs">
            <div>
              <p className="text-[10px] text-text-muted">Umbral Ventas Promedio</p>
              <p className="font-bold text-text">{data.thresholds.avgVolume} unds</p>
            </div>
            <div className="h-6 w-px bg-border" />
            <div>
              <p className="text-[10px] text-text-muted">Umbral Margen Promedio</p>
              <p className="font-bold text-text">{formatCOP(data.thresholds.avgMargin)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tarjetas Resumen de los 4 Cuadrantes ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Estrellas */}
        <button
          type="button"
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'STAR' ? 'ALL' : 'STAR')}
          className={`text-left rounded-3xl border p-4 sm:p-5 transition-all ${
            selectedQuadrant === 'STAR'
              ? 'border-emerald-500 bg-emerald-500/10 shadow-md ring-2 ring-emerald-500/20'
              : 'border-border bg-surface hover:border-emerald-500/40'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">⭐</span>
            <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
              {data.summary.starsCount} platos
            </span>
          </div>
          <h3 className="text-sm font-bold text-text">Estrellas</h3>
          <p className="text-[11px] text-text-muted mt-0.5">Alta Venta + Alto Margen</p>
          <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium mt-2">
            Mantener calidad y recetas fijas
          </p>
        </button>

        {/* Caballos de Batalla */}
        <button
          type="button"
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'PLOWHORSE' ? 'ALL' : 'PLOWHORSE')}
          className={`text-left rounded-3xl border p-4 sm:p-5 transition-all ${
            selectedQuadrant === 'PLOWHORSE'
              ? 'border-blue-500 bg-blue-500/10 shadow-md ring-2 ring-blue-500/20'
              : 'border-border bg-surface hover:border-blue-500/40'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🐴</span>
            <span className="rounded-full bg-blue-500/15 px-2.5 py-0.5 text-[11px] font-bold text-blue-600 dark:text-blue-400">
              {data.summary.plowhorsesCount} platos
            </span>
          </div>
          <h3 className="text-sm font-bold text-text">Caballos de Batalla</h3>
          <p className="text-[11px] text-text-muted mt-0.5">Alta Venta + Bajo Margen</p>
          <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium mt-2">
            Subir precio o ajustar porciones
          </p>
        </button>

        {/* Rompecabezas */}
        <button
          type="button"
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'PUZZLE' ? 'ALL' : 'PUZZLE')}
          className={`text-left rounded-3xl border p-4 sm:p-5 transition-all ${
            selectedQuadrant === 'PUZZLE'
              ? 'border-amber-500 bg-amber-500/10 shadow-md ring-2 ring-amber-500/20'
              : 'border-border bg-surface hover:border-amber-500/40'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🧩</span>
            <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-bold text-amber-600 dark:text-amber-400">
              {data.summary.puzzlesCount} platos
            </span>
          </div>
          <h3 className="text-sm font-bold text-text">Rompecabezas</h3>
          <p className="text-[11px] text-text-muted mt-0.5">Baja Venta + Alto Margen</p>
          <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium mt-2">
            Promocionar con los meseros
          </p>
        </button>

        {/* Perros */}
        <button
          type="button"
          onClick={() => setSelectedQuadrant(selectedQuadrant === 'DOG' ? 'ALL' : 'DOG')}
          className={`text-left rounded-3xl border p-4 sm:p-5 transition-all ${
            selectedQuadrant === 'DOG'
              ? 'border-rose-500 bg-rose-500/10 shadow-md ring-2 ring-rose-500/20'
              : 'border-border bg-surface hover:border-rose-500/40'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🐕</span>
            <span className="rounded-full bg-rose-500/15 px-2.5 py-0.5 text-[11px] font-bold text-rose-600 dark:text-rose-400">
              {data.summary.dogsCount} platos
            </span>
          </div>
          <h3 className="text-sm font-bold text-text">Perros</h3>
          <p className="text-[11px] text-text-muted mt-0.5">Baja Venta + Bajo Margen</p>
          <p className="text-[10px] text-rose-600 dark:text-rose-400 font-medium mt-2">
            Evaluar retiro de la carta
          </p>
        </button>
      </div>

      {/* ── Filtros de Vista ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-text-muted">Filtrar por:</span>
          <div className="flex flex-wrap gap-1.5">
            {[
              { id: 'ALL', label: `Todos (${data.items.length})` },
              { id: 'STAR', label: '⭐ Estrellas' },
              { id: 'PLOWHORSE', label: '🐴 Caballos' },
              { id: 'PUZZLE', label: '🧩 Rompecabezas' },
              { id: 'DOG', label: '🐕 Perros' },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelectedQuadrant(f.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                  selectedQuadrant === f.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-surface border border-border text-text-muted hover:text-text'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Listado de Tarjetas Tácticas de Productos ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredItems.map((item) => {
          const badgeClass =
            item.quadrant === 'STAR'
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
              : item.quadrant === 'PLOWHORSE'
              ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30'
              : item.quadrant === 'PUZZLE'
              ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30'
              : 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/30';

          return (
            <div
              key={item.id}
              className="flex flex-col justify-between rounded-3xl border border-border bg-surface p-5 shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
            >
              <div>
                {/* Header de tarjeta */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <h4 className="font-bold text-text text-sm sm:text-base leading-snug">{item.name}</h4>
                    <p className="text-[11px] text-text-muted mt-0.5">
                      {item.hasRecipe ? '✓ Con escandallo de receta' : '⚠️ Costo estándar estimado (30%)'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold ${badgeClass}`}>
                    {item.quadrantLabel}
                  </span>
                </div>

                {/* Métricas clave */}
                <div className="grid grid-cols-3 gap-2 rounded-2xl bg-surface-muted/60 p-3 text-xs mb-3">
                  <div>
                    <p className="text-[10px] text-text-muted">Precio Venta</p>
                    <p className="font-bold text-text">{formatCOP(item.price)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-muted">Costo Insumos</p>
                    <p className="font-semibold text-rose-500">{formatCOP(item.cost)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-muted">Margen Unitario</p>
                    <p className="font-bold text-emerald-500">
                      {formatCOP(item.margin)} ({item.marginPct}%)
                    </p>
                  </div>
                </div>

                {/* Rendimiento global del período */}
                <div className="flex items-center justify-between text-xs px-1 mb-3">
                  <div>
                    <span className="text-text-muted">Vendidos:</span>{' '}
                    <span className="font-bold text-text">{item.quantity} unds</span>
                  </div>
                  <div>
                    <span className="text-text-muted">Ganancia bruta total:</span>{' '}
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCOP(item.totalProfit)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recomendación de negocio */}
              <div className="rounded-2xl border border-border/80 bg-surface-muted/40 p-3 text-xs">
                <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                  Estrategia Recomendada:
                </p>
                <p className="text-text leading-relaxed text-[11px]">{item.recommendation}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
